import { useState, Element, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { PaymentFields } from '../../../../../../../src/interfaces/WoocommercePaymentBlockSupport';
import CardsSelectBox from './CardsSelectBox';
import RegisteredCards from './RegisteredCards';

/**
 * Go through the props documentation for more information.
 *
 * @see https://github.com/woocommerce/woocommerce/blob/9c58f198cfb7f1413bc83775fa2d9cc5bd5775fb/plugins/woocommerce-blocks/packages/components/radio-control-accordion/stories/index.stories.tsx#L12
 * @see https://github.com/woocommerce/woocommerce/blob/9c58f198cfb7f1413bc83775fa2d9cc5bd5775fb/plugins/woocommerce-blocks/packages/components/radio-control-accordion/index.tsx#L13
 */
// @ts-ignore
const PaymentRadio = window.wc.blocksComponents.RadioControlAccordion;
type Option = {
    value: string;
    label: string | Element;
    onChange: ( value: string ) => void;
    name: string;
    content: Element;
};

function Content( props ) {
    const [ paymentType, setPaymentType ] = useState< string >( '' );
    const [ paymentOptions, setPaymentOptions ] = useState< Option[] >( [] );
    const [ js, setJs ] = useState< string >( '' );
    const [ lang, setLang ] = useState< string >( '' );
    const [ colorDepth, setColorDepth ] = useState< string | number >( '' );
    const [ screenHeight, setScreenHeight ] = useState< string | number >( '' );
    const [ screenWidth, setScreenWidth ] = useState< string | number >( '' );
    const [ timezoneOffset, setTimezoneOffset ] = useState< string >( '' );
    const [ userAgent, setUserAgent ] = useState< string >( '' );
    const [ dokanMangopay3ds2Java, setdokanMangopay3ds2Java ] =
        useState< string >( '' );
    const [ selectedCard, setSelectedCard ] = useState< string >( '' );

    const settings: PaymentFields = props?.settings ?? {};
    const { eventRegistration, emitResponse } = props;
    const { onPaymentSetup, onPaymentProcessing } = eventRegistration;

    const radioClickHandler = ( value: string ) => {
        if ( 'bank_wire' === value ) {
            setSelectedCard( '' );
        }
    };

    const validateCreditItem = ( item: object ) => {
        // @ts-ignore
        return (
            'BANK_WIRE' !== item.key &&
            settings?.selected_credit_cards.includes( item.key )
        );
    };

    const validateDebitItem = ( item: object ) => {
        // @ts-ignore
        return settings?.selected_debit_cards.includes( item.key );
    };

    const onSelectCard = ( value ) => {
        setSelectedCard( value );
    };

    const addPaymentGateway = () => {
        let defaultType = '';
        const updatedOptions = [ ...paymentOptions ];
        const selectedCreditcards: string[] = Array.isArray(
            settings?.selected_credit_cards
        )
            ? settings.selected_credit_cards
            : [];

        /**
         * Adding credit card option to the list.
         */
        if (
            settings?.credit_card_activated &&
            settings?.credit_card_enabled
        ) {
            const creditCardLabel: string = settings?.credit_card_enabled
                ? __( 'Credit Card', 'dokan' )
                : __( 'Credit Card Type', 'dokan' );
            const creditCardValue = 'card';

            const payment: Option = {
                value: creditCardValue,
                label: creditCardLabel,
                onChange: radioClickHandler,
                name: 'dokan_mangopay_payment_type_credit_card',
                content: (
                    <CardsSelectBox
                        cards={ settings?.available_card_types ?? {} }
                        selectedCards={ settings?.selected_credit_cards ?? {} }
                        validateItem={ validateCreditItem }
                        onChange={ onSelectCard }
                    />
                ),
            };

            defaultType = String( defaultType ).length
                ? defaultType
                : creditCardValue;
            updatedOptions.push( payment );
        }

        /**
         * Adding direct debit web wire card option to the list.
         */
        if (
            settings?.debit_card_enabled &&
            ( settings?.credit_card_enabled ||
                selectedCreditcards.includes( 'BANK_WIRE' ) )
        ) {
            const debitCardLabel =
                settings?.credit_card_enabled ||
                selectedCreditcards.includes( 'BANK_WIRE' )
                    ? __( 'Direct Debit Web Wire', 'dokan' )
                    : __( 'Payment Type', 'dokan' );
            const debitCardValue = 'directdebitweb';

            const payment: Option = {
                value: debitCardValue,
                label: debitCardLabel,
                onChange: radioClickHandler,
                name: 'dokan_mangopay_payment_type_debit_card',
                content: (
                    <CardsSelectBox
                        cards={ settings?.available_direct_payments ?? {} }
                        selectedCards={ settings?.selected_debit_cards ?? {} }
                        validateItem={ validateDebitItem }
                        onChange={ onSelectCard }
                    />
                ),
            };

            defaultType = String( defaultType ).length
                ? defaultType
                : debitCardValue;
            updatedOptions.push( payment );
        }

        /**
         * Adding direct bank wire option to the list.
         */
        if (
            selectedCreditcards.includes( 'BANK_WIRE' ) &&
            ( settings?.credit_card_activated || settings?.debit_card_enabled )
        ) {
            const value = 'bank_wire';
            const label = __( 'Direct Bank Wire', 'dokan' );

            const payment: Option = {
                value,
                label,
                onChange: radioClickHandler,
                name: 'dokan_mangopay_payment_type_bank_wire',
                content: <></>,
            };

            defaultType = String( defaultType ).length ? defaultType : value;
            updatedOptions.push( payment );
        }

        /**
         * Adding registered cards option to the list.
         */
        if (
            settings?.saved_cards_enabled &&
            settings?.mangopay_account_id &&
            String( settings.mangopay_account_id ).length
        ) {
            const value = 'registeredcard';
            const label = __( 'Registered Card', 'dokan' );

            const payment: Option = {
                value,
                label,
                onChange: radioClickHandler,
                name: 'dokan_mangopay_payment_type_registeredcard',
                content: <RegisteredCards onChange={ onSelectCard } />,
            };

            defaultType = String( defaultType ).length ? defaultType : value;
            updatedOptions.push( payment );
        }

        setPaymentType( defaultType );
        setPaymentOptions( updatedOptions );
    };

    useEffect( () => {
        setJs( 'yes' );
        setLang( window.navigator.language );
        setColorDepth( window.screen.colorDepth );
        setScreenHeight( window.screen.height );
        setScreenWidth( window.screen.width );
        setTimezoneOffset( new Date().getTimezoneOffset().toString() );
        setUserAgent( window.navigator.userAgent );
        setdokanMangopay3ds2Java(
            window.navigator.javaEnabled() ? 'yes' : 'no'
        );

        addPaymentGateway();
    }, [] );

    const onSubmit = () => {
        const paymentMethodData = {
            dokan_mangopay_payment_type: paymentType,
            'dokan-mangopay-3ds2-java': String( dokanMangopay3ds2Java ),
            'dokan-mangopay-3ds2-js': String( js ),
            'dokan-mangopay-3ds2-lang': String( lang ),
            'dokan-mangopay-3ds2-color-depth': String( colorDepth ),
            'dokan-mangopay-3ds2-screen-height': String( screenHeight ),
            'dokan-mangopay-3ds2-screen-width': String( screenWidth ),
            'dokan-mangopay-3ds2-timezone-offset': String( timezoneOffset ),
            'dokan-mangopay-3ds2-user-agent': String( userAgent ),
        };

        let error = '';

        if ( 'card' === paymentType ) {
            paymentMethodData['dokan_mangopay_card_type'] = selectedCard;
        } else if ( 'directdebitweb' === paymentType ) {
            paymentMethodData['dokan_mangopay_directdebitweb_type'] = selectedCard;
        } else if ( 'registeredcard' === paymentType ) {
            paymentMethodData['registered_card_selected'] = selectedCard;

            if ( ! selectedCard ) {
                error = __(
                    'Could not create the MangoPay payment. Please select a registered card or add one.',
                    'dokan'
                );
            }
        }

        if ( error ) {
            return {
                type: emitResponse.responseTypes.ERROR,
                message: error,
            };
        }

        return {
            type: emitResponse.responseTypes.SUCCESS,
            meta: {
                paymentMethodData,
            },
        };
    };

    useEffect( () => {
        const unsubscribe = onPaymentSetup( onSubmit );

        return () => unsubscribe();
    }, [ paymentType, selectedCard ] );

    return (
        <div className="mangopay-block-description">
            <PaymentRadio
                instanceId={ Date.now() }
                className="mangopay-radio-block"
                id="dokan_mangopay_payment_type"
                onChange={ ( value: string ) => {
                    setPaymentType( value );
                } }
                options={ paymentOptions }
                selected={ paymentType }
            />
        </div>
    );
}

export default Content;
